import { Add, DeleteOutline } from '@mui/icons-material';
import { Box, Divider, IconButton, TextField } from '@mui/material';
import type { CSSProperties } from 'react';
import {
  Controller,
  get,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import { HTTP_URL_PATTERN } from '../../../helpers/knowledgeHelper';
import { useTranslation } from '../../../hooks/useTranslation';
import Button from '../Button/Button';
import Text from '../Text/Text';
import ToolTip from '../ToolTip/ToolTip';

// Compact secondary action used for list builders (add page / URL / digest).
// Overrides StyledButton's default 44px small height so these sit inline with
// form labels instead of reading like primary CTAs.
const addRowButtonStyle: CSSProperties = {
  textTransform: 'none',
  height: 'auto',
  minHeight: '36px',
  gap: '0.35rem',
  borderColor: 'var(--primary-color)',
  color: 'var(--primary-color)',
  backgroundColor: '#FFFFFF',
  fontSize: '0.8125rem',
  letterSpacing: '0.01em',
};

// A config int that must be 0 or more, matching the backend's own
// `_non_negative_int` check - truncates as the user types so an invalid
// value (negative, fractional) is never something they can save. Empty
// stays undefined so the entry is sent as a plain URL (backend default
// depth) rather than an explicit 0.
const clampNonNegativeInt = (rawValue: string): number | undefined => {
  if (rawValue === '') return undefined;
  const parsed = Number(rawValue);
  if (Number.isNaN(parsed)) return undefined;
  return Math.max(0, Math.trunc(parsed));
};

// Registers webscrap_links as an array of { url, depth, splitSelector } rows,
// on top of the single Website URL sweep above - each row is crawled to its
// own depth, and optionally split into one document per element matching its
// CSS selector (e.g. one document per FAQ answer) instead of one document
// for the whole page. Matches the backend's WebscrapConfig.links/
// split_selectors (see libs/webscrap/config.py LinkRule and
// libs/webscrap/extract.py split_page_by_selector).
const WebscrapLinksField = () => {
  const { t } = useTranslation();
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'webscrap_links',
  });

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <Text type="label">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            {t('Extra pages to crawl')}
            <ToolTip
              title={t(
                'Pin specific pages to crawl on top of the website above, each to its own depth.'
              )}
            />
          </Box>
        </Text>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          size="small"
          padding="0.35rem 0.85rem"
          style={addRowButtonStyle}
          onClick={() =>
            append({ url: '', depth: undefined, splitSelector: '' })
          }
        >
          <Add sx={{ fontSize: '1rem' }} />
          {t('Add page')}
        </Button>
      </Box>

      {fields.map((field, index) => {
        const urlError = get(errors, `webscrap_links.${index}.url`);
        return (
          <Box key={field.id} sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Numbered divider between entries, matching DigestsConfigFields'
              pattern - gives each page its own clearly bounded group instead
              of two loosely-stacked rows per entry with no separation. */}
            <Divider
              sx={{
                marginTop: index === 0 ? '0.75rem' : '1rem',
                marginBottom: '0.75rem',
                fontSize: '0.75rem',
                color: '#94A3B8',
                '&::before, &::after': {
                  borderColor: 'rgba(15, 23, 42, 0.1)',
                },
              }}
            >
              {index + 1}
            </Divider>

            <Box
              sx={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}
              >
                <Box>
                  <Text type="label">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}
                    >
                      {t('Page URL')}
                      <ToolTip
                        title={t(
                          'The page to crawl in addition to the website above.'
                        )}
                      />
                    </Box>
                  </Text>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://example.com/pricing"
                    inputProps={{
                      'aria-label': t('Extra page URL {{number}}', {
                        number: index + 1,
                      }),
                    }}
                    error={!!urlError}
                    helperText={
                      urlError
                        ? urlError.type === 'pattern'
                          ? t(
                              'Enter a full URL starting with http:// or https://'
                            )
                          : t('Enter the page URL, or remove this page')
                        : undefined
                    }
                    {...register(`webscrap_links.${index}.url`, {
                      required: true,
                      pattern: HTTP_URL_PATTERN,
                    })}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                  <Box sx={{ width: '6.5rem', flexShrink: 0 }}>
                    <Text type="label">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        {t('Depth')}
                        <ToolTip
                          title={t(
                            'How many links deep the scraper follows from this page.'
                          )}
                        />
                      </Box>
                    </Text>
                    <Controller
                      name={`webscrap_links.${index}.depth`}
                      control={control}
                      render={({ field: depthField }) => (
                        <TextField
                          type="number"
                          size="small"
                          fullWidth
                          value={depthField.value ?? ''}
                          onChange={event =>
                            depthField.onChange(
                              clampNonNegativeInt(event.target.value)
                            )
                          }
                          onBlur={depthField.onBlur}
                          inputProps={{
                            min: 0,
                            step: 1,
                            'aria-label': t('Extra page depth {{number}}', {
                              number: index + 1,
                            }),
                          }}
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Text type="label">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                        }}
                      >
                        {t('Split selector')}
                        <ToolTip
                          title={t(
                            'Optional CSS selector matching each section of this page (e.g. a FAQ answer). When set, the page is split into one document per matching section instead of one document for the whole page.'
                          )}
                        />
                      </Box>
                    </Text>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder=".faq-section"
                      inputProps={{
                        'aria-label': t('Split selector for page {{number}}', {
                          number: index + 1,
                        }),
                      }}
                      {...register(`webscrap_links.${index}.splitSelector`)}
                    />
                  </Box>
                </Box>
              </Box>

              <IconButton
                aria-label={t('Remove page')}
                onClick={() => remove(index)}
                size="small"
                type="button"
                sx={{ color: '#94A3B8', mt: '1.5rem' }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default WebscrapLinksField;
